import { useState } from 'react';
import { Field, Input, Textarea } from '@/ui';
import { usersApi } from '@/api';
import type { User } from '@/types';
import { StepLayout } from '../StepLayout';

interface PaymentStepProps {
  profile: User;
  onSaved: (user: User) => void;
  onSkip: () => void;
}

// Inspectors collect payment via whatever provider they already use — Square,
// Stripe Payment Link, QuickBooks, PayPal, Venmo Business, etc. We capture
// three optional fields:
//   paymentMethodUrl    — clickable URL the email CTA links to
//   paymentMethodLabel  — short button text ("Pay via Square")
//   paymentInstructions — free-form copy ("Mail check to ...", account
//                         numbers, alternate options) — rendered as plain
//                         text under the button.
// Any combination is valid; skipping is fine. The data lands on the user
// row and surfaces in the report-delivery and agreement-signed emails.
export function PaymentStep({ profile, onSaved, onSkip }: PaymentStepProps) {
  const [url, setUrl] = useState(profile.paymentMethodUrl ?? '');
  const [label, setLabel] = useState(profile.paymentMethodLabel ?? '');
  const [instructions, setInstructions] = useState(profile.paymentInstructions ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleNext = async () => {
    const trimmedUrl = url.trim();
    const trimmedLabel = label.trim();
    const trimmedInstructions = instructions.trim();

    if (!trimmedUrl && !trimmedLabel && !trimmedInstructions) {
      onSkip();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const resp = await usersApi.updateProfile({
        paymentMethodUrl: trimmedUrl,
        paymentMethodLabel: trimmedLabel,
        paymentInstructions: trimmedInstructions,
      });
      if (resp.success && resp.data) {
        onSaved(resp.data);
      } else {
        setError(resp.error || 'Failed to save.');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <StepLayout
      title="How clients pay you"
      subtitle="Drop in the payment link you already use — Square, Stripe Payment Link, QuickBooks invoice, PayPal.me, anything that accepts a URL. Clients will see a button in the report-delivery email; you keep your existing payment workflow."
      onPrimary={handleNext}
      primaryLabel="Save & continue"
      primaryLoading={saving}
      onSecondary={onSkip}
      secondaryLabel="Skip for now"
      error={error}
    >
      <div className="flex flex-col gap-4">
        <Field
          label="Payment link"
          hint="Must be https://. Leave blank if you'd rather only show free-form instructions."
        >
          <Input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://squareup.com/pay/..."
            autoComplete="off"
          />
        </Field>
        <Field
          label="Button label (optional)"
          hint="Short text shown on the button — falls back to “Pay your inspector”."
        >
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Pay via Square"
            maxLength={50}
          />
        </Field>
        <Field
          label="Other instructions (optional)"
          hint="Anything else the client should know — alternate payment methods, where to mail a check, etc. Plain text only."
        >
          <Textarea
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            rows={5}
            maxLength={2000}
            placeholder={'Venmo: @acme-inspections\nZelle: pay@acmeinspections.com\nOr mail a check to: PO Box 123, Anytown, OR 97000'}
            className="font-mono text-body-sm"
          />
        </Field>
      </div>
    </StepLayout>
  );
}
